import FadeIn from "@/components/FadeIn";
import Parallax from "@/components/Parallax";
import { DotWaterhole, DotUShape } from "@/components/AboriginalPatterns";
import { Layers } from "lucide-react";
import PullQuote from "@/components/PullQuote";

const SectionBadge = ({ icon: Icon, label, color = "primary" }: { icon: any; label: string; color?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`p-2.5 rounded-2xl bg-${color}/8 border border-${color}/15`}>
      <Icon size={16} className={`text-${color}`} />
    </div>
    <span className={`text-[10px] font-semibold uppercase tracking-[0.3em] text-${color}`}>{label}</span>
  </div>
);

const WhereRiskSection = () => (
  <section className="relative section-padding bg-card/80 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-ochre/[0.02]" />
    <Parallax className="absolute -bottom-16 -left-16" offset={50} offsetX={-20}>
      <DotWaterhole className="text-ochre opacity-12" size={450} />
    </Parallax>
    <Parallax className="absolute top-16 left-10" offset={25}>
      <DotUShape className="text-ochre opacity-15" size={70} />
    </Parallax>
    <div className="relative max-w-3xl mx-auto px-6">
      <FadeIn>
        <SectionBadge icon={Layers} label="Where Risk Forms" color="ochre" />
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 tracking-tight">
          One Layer Below the Executive Team
        </h2>
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
          <p>Inside the company you are acquiring, execution risk rarely concentrates in the C-suite.</p>
          <p>It builds in the day-to-day leadership environment where managers:</p>
          <ul className="space-y-2 pl-1">
            {["Set priorities", "Escalate decisions", "Interpret standards", "Exercise authority", "Deliver feedback"].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ochre/60 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p>
            It shows up as <span className="text-foreground font-medium">patterns, not people</span>.
          </p>
          <div className="pl-4 border-l-2 border-border/30 space-y-3 text-muted-foreground/80">
            <p>Standards gradually lowered without anyone noticing.</p>
            <p>An unspoken agreement to avoid difficult conversations.</p>
            <p>Unclear ownership over who decides what.</p>
            <p>Managers applying rules differently across teams.</p>
            <p>Problems flagged but never resolved.</p>
          </div>
          <p className="text-foreground/70">
            Invisible when revenue is growing.
          </p>
          <p className="text-foreground font-medium">
            Expensive when pressure increases.
          </p>
        </div>
      </FadeIn>
      <PullQuote text="The patterns that matter most are the ones no one is watching." />
    </div>
  </section>
);

export default WhereRiskSection;
